import { useMemo, useState } from 'react';
import { buildQueryAst } from '../../common';
import {
  PillsInputViewPropsType,
  SuggestionsDropdownPropsType,
} from '../../components';
import {
  QueryBuilderAstNodeType,
  QueryBuilderEntityConfigType,
  QueryBuilderInputContextType,
  QueryBuilderTypingCommitActionType,
} from '../../types';
import {
  canCloseBracket,
  canOpenBracket,
  getActiveEntityTokenIndex,
  getInputContext,
  getSuggestionOptions,
  mapTokensToPillViews,
  resolveBracketInsertFromSearch,
  resolveTypingCommit,
  shouldShowInvalidHint,
} from './helpers';
import {
  useDebouncedAstSubmit,
  useEntityValueOptions,
  useQueryBuilderTokens,
} from './hooks';

type UseQueryBuilderArgsType = {
  entities: QueryBuilderEntityConfigType[];
  onChange: (ast: ReturnType<typeof buildQueryAst>) => void;
};

type UseQueryBuilderReturnType = {
  ast: QueryBuilderAstNodeType | null;
  context: QueryBuilderInputContextType;
  handleTypingCommitAction: (
    action: QueryBuilderTypingCommitActionType
  ) => void;
  isSubmitDisabled: boolean;
  loadAst: (ast: QueryBuilderAstNodeType) => void;
  pillsInputProps: PillsInputViewPropsType;
  suggestionsDropdownProps: SuggestionsDropdownPropsType;
};

export const useQueryBuilder = ({
  entities,
  onChange,
}: UseQueryBuilderArgsType): UseQueryBuilderReturnType => {
  const [search, setSearch] = useState('');

  const clearSearch = () => {
    setSearch('');
  };

  const {
    tokens,
    editTokenIndex,
    pendingEntity,
    isDragDisabled,
    handleTypingCommitAction,
    handleOpenBracket,
    handleCloseBracket,
    handleRemoveTokenAtIndex,
    handleReorderTokens,
    handleConditionPillClick,
    handleClearAll,
    loadAst,
  } = useQueryBuilderTokens({
    entities,
    onChange,
    onCommit: clearSearch,
  });

  const ast = useMemo(() => buildQueryAst(tokens), [tokens]);

  useDebouncedAstSubmit({
    ast,
    onChange,
  });

  const context = getInputContext(tokens, editTokenIndex);
  const activeEntityTokenIndex = getActiveEntityTokenIndex(
    tokens,
    editTokenIndex
  );
  const activeTokenIndex = editTokenIndex ?? activeEntityTokenIndex;
  const canOpenBracketOption = canOpenBracket(tokens);
  const canCloseBracketOption = canCloseBracket(tokens);

  const { valueOptions, isLoadingValues } = useEntityValueOptions({
    context,
    pendingEntity,
    search,
  });

  const {
    entityOptions,
    filteredEntityOptions,
    filteredOperatorOptions,
    filteredValueOptions,
  } = getSuggestionOptions({
    context,
    entities,
    search,
    valueOptions,
  });

  const showInvalidHint = shouldShowInvalidHint({
    tokenCount: tokens.length,
    ast,
  });

  const handleSearchChange = (nextSearch: string) => {
    const bracketInsert = resolveBracketInsertFromSearch(
      context,
      nextSearch,
      canOpenBracketOption,
      canCloseBracketOption
    );

    if (bracketInsert === 'open') {
      handleOpenBracket();
      return;
    }

    if (bracketInsert === 'close') {
      handleCloseBracket();
      return;
    }

    setSearch(nextSearch);
  };

  const handleEnterCommit = (highlightedIndex = 0) => {
    const commitAction = resolveTypingCommit({
      context,
      search,
      entities: entityOptions,
      valueOptions,
      canOpenBracket: canOpenBracketOption,
      canCloseBracket: canCloseBracketOption,
      highlightedIndex,
    });

    if (commitAction) {
      handleTypingCommitAction(commitAction);
    }
  };

  const pills = mapTokensToPillViews({
    tokens,
    entities,
    activeTokenIndex,
  });

  return {
    ast,
    context,
    handleTypingCommitAction,
    isSubmitDisabled: showInvalidHint,
    loadAst,
    pillsInputProps: {
      pills,
      search,
      showInvalidHint,
      isDragDisabled,
      onClearAll: handleClearAll,
      onRemovePillAtIndex: handleRemoveTokenAtIndex,
      onReorderPills: handleReorderTokens,
      onPillClick: handleConditionPillClick,
      onSearchChange: handleSearchChange,
      onEnterCommit: handleEnterCommit,
    },
    suggestionsDropdownProps: {
      context,
      search,
      filteredEntityOptions,
      filteredOperatorOptions,
      filteredValueOptions,
      isLoadingValues: context === 'expectValue' && isLoadingValues,
      canOpenBracketOption,
      canCloseBracketOption,
      pendingEntityKey: pendingEntity?.key ?? null,
    },
  };
};
